// @flow

import LinkButton from 'components/LinkButton/LinkButton'
import { Table, Row, Cell } from 'components/Table'
import { withRouter } from 'lib/hooks/withRouter'
import { httpGet } from 'lib/http'
import { setBanner, setLoad, setUnload } from 'lib/notification/actions'
import moment from 'moment'
import queryString from 'query-string'
import Pagination from 'rc-pagination'
import 'rc-pagination/assets/index.less'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs'
import { decodedData } from '../../../lib/encrypt'
import { colorTheme } from '../Distributions/components/colors/color'
import { navigateWithoutRedirect } from '../Distributions/components/Queue/configuration'
import { filterList } from '../Distributions/components/Queue/constant'
import { sortByKey } from '../Distributions/components/Queue/sort'
import styles from '../Distributions/components/Queue/styles'
import TableFilter from '../Distributions/components/Queue/TableFilter'
import { LockUnlockIcon } from '../Distributions/components/permissions'
import { headers } from './Headers'
import { getQueueNameFromStatus } from './Forms/constant.js'

const Deposits = ({ location }) => {
  const dispatch = useDispatch()
  const { query, navigate } = location

  const tableId = query.get('tid') ? decodedData(query.get('tid')) : null
  const subTableId = query.get('stid') ? decodedData(query.get('stid')) : null

  const [counts, setCounts] = useState({})
  const [deposits, setDeposits] = useState([])
  const [filteredDeposits, setFilteredDeposits] = useState([])

  const getAllTabsForStatus = (status) => {
    switch (status) {
      case 'deposit_queue':
        return [
          { title: 'RECEIVE DEPOSIT', countKey: 'received', nextStatus: 'received' },
          { title: 'TRANSFERS NEEDED', countKey: 'transferred', nextStatus: 'transferred' },
          { title: 'REVIEW DEPOSIT', countKey: 'reviewed', nextStatus: 'reviewed' },
          { title: 'SECOND REVIEW', countKey: 'reviewed_with_approved_edits', nextStatus: 'reviewed_with_approved_edits' },
          { title: 'DEPOSITED TODAY', countKey: 'deposited_today', nextStatus: 'deposited_today' },
        ]
      case 'all_deposits':
        return [{ title: 'ALL', countKey: 'all', nextStatus: 'deposited' }]
      case 'cash':
        return [{ title: 'CASH DEPOSITS', countKey: 'cash', nextStatus: 'cash' }]
      case 'deleted':
        return [{ title: 'DELETED DEPOSITS', countKey: 'deleted', nextStatus: 'deleted' }]
      default:
        return [{ title: 'ALL', countKey: 'all', nextStatus: 'deposited' }]
    }
  }

  const getInitialStatus = (tableId, tabIndex) => {
    const currentStatus = tableId || 'deposit_queue'
    const tabs = getAllTabsForStatus(currentStatus)
    return tabs[tabIndex]?.nextStatus || null
  }

  const [nextStatus, setNextStatus] = useState(() => {
    const initialTableId = tableId || 'deposit_queue'
    const initialTabIndex = subTableId ? parseInt(subTableId, 10) : 0
    return getInitialStatus(initialTableId, initialTabIndex)
  })

  const [currentStatus, setCurrentStatus] = useState(tableId || 'deposit_queue')
  const [filterKey, setFilterKey] = useState(null)
  const [sort, setSort] = useState({ key: null, type: null, column: null })

  const [selectedTabIndex, setSelectedTabIndex] = useState(() => {
    return subTableId ? parseInt(subTableId, 10) : 0
  })

  const [color, setColor] = useState('green')
  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 100,
    total: 0,
  })
  const [selectedOptions, setSelectedOptions] = useState(['ALL TRUSTS'])
  const [dataLoaded, setDataLoaded] = useState(false)

  const dateFormat = (d) => moment(d).format('dddd MMMM D, YYYY')

  const summaryBoxNavList = () => [
    { countKey: 'deposit_queue', title: 'DEPOSIT QUEUE', theme: 'green' },
    { countKey: 'deleted', title: 'DELETED', theme: 'orange' },
    { countKey: 'all_deposits', title: 'ALL DEPOSITS', theme: 'cyan' },
    { countKey: 'cash', title: 'CASH', theme: 'green' },
  ]

  const tabTitleList = (currentStatus) => {
    if (!dataLoaded) {
      return []
    }

    const allTabs = getAllTabsForStatus(currentStatus)

    if (currentStatus === 'deposit_queue') {
      return allTabs.filter((tab) => {
        if (tab.countKey === 'reviewed_with_approved_edits') {
          return counts.reviewed_with_approved_edits && counts.reviewed_with_approved_edits > 0
        }
        return true
      })
    }

    return allTabs
  }

  const getNewStatus = (currentStatus, tabIndex) => {
    const tabs = tabTitleList(currentStatus)
    return tabs[tabIndex]?.nextStatus || null
  }

  const getJournalNameFromSelection = (selectedOptions) => {
    if (!selectedOptions || selectedOptions.length === 0) {
      return 'A'
    }

    if (selectedOptions.includes('ALL TRUSTS') || selectedOptions.includes('ALL_TRUSTS')) {
      return 'A'
    }

    return selectedOptions[0]
  }

  const resetFilter = () => {
    setSort({ key: null, type: null, column: null })
    setFilterKey(null)
    setSelectedOptions(filterList[0].value)
  }

  const handleTabSelect = (index) => {
    const tabIndex = Number(index)
    setSelectedTabIndex(tabIndex)
    const newStatus = getNewStatus(currentStatus, tabIndex)
    setNextStatus(newStatus)

    navigateWithoutRedirect('stid', tabIndex)
  }

  const tabTitle = (title, countKey) => {
    let fullTitle = title + ' ('
    if (counts.hasOwnProperty(countKey)) {
      fullTitle += counts[countKey]
    } else {
      fullTitle += '0'
    }
    return fullTitle + ')'
  }

  const isNavActive = (isActive) =>
    isActive
      ? `${styles.navItemActive} ${styles.navItem} ${colorTheme[color].textDark} ${colorTheme[color].borderMid ?? colorTheme[color].borderLight}`
      : styles.navItem

  const handleCurrentStatus = (key, click) => {
    let status = null
    if (key === 'deposit_queue') {
      status = 'received'
    } else if (key === 'all_deposits') {
      status = 'deposited'
    } else if (key === 'cash') {
      status = 'cash'
    } else if (key === 'deleted') {
      status = 'deleted'
    }
    setNextStatus(status)
    setCurrentStatus(key)

    if (click) {
      setSelectedTabIndex(0)
      navigateWithoutRedirect('stid', 0)
    }

    const foundColor = summaryBoxNavList().find((li) => li.countKey === key)
    foundColor?.theme && setColor(foundColor.theme)
    navigateWithoutRedirect('tid', key)

    if (click) {
      resetFilter()
    }
  }

  const handleFilter = (key, id) => {
    setSort((prev) => {
      let options = {
        key,
        id: id,
        type: prev.type === 'asc' || !prev.type ? 'desc' : 'asc',
      }
      if (prev.key != key) {
        options.type = 'asc'
      }
      return options
    })
  }

  const navigateToDeposit = (e) => {
    e.preventDefault()
    if (!search || search.trim() === '') return

    const queueName = getQueueNameFromStatus(nextStatus)
    navigate(`/deposits/${search}?from_queue=${queueName}`)
  }

  const getData = () => {
    const journalName = getJournalNameFromSelection(selectedOptions)

    const queryParams = queryString.stringify({
      next_status: nextStatus,
      page: pagination.current,
      journal_name: journalName,
    })
    const url = '/admin/deposits?' + queryParams

    dispatch(setLoad())

    httpGet(url)
      .then((response) => {
        let deposits = []
        let counts = {}

        if (response && response.data && response.data.deposits) {
          deposits = response.data.deposits
          counts = response.data.counts
        } else if (response && response.deposits) {
          deposits = response.deposits
          counts = response.counts
        } else if (Array.isArray(response)) {
          deposits = response
        } else {
          throw new Error('Invalid response structure')
        }

        setDeposits(deposits)
        setFilteredDeposits(sortByKey(deposits.map(transformApiDeposit), sort.key || 'inserted_at', sort.type || 'desc'))

        const calculatedCounts = {
          received: counts.received || 0,
          transferred: counts.transferred || 0,
          reviewed: counts.reviewed || 0,
          reviewed_with_approved_edits: counts.reviewed_with_approved_edits || 0,
          deposited_today: counts.deposited_today || 0,
          all: counts.deposited || counts.all || 0,
          cash: counts.cash || 0,
          deleted: counts.deleted || 0,
          deposited: counts.deposited || 0,
        }

        setCounts(calculatedCounts)
        setPagination((prev) => ({ ...prev, total: calculatedCounts[nextStatus] || calculatedCounts.all || 0 }))
        setDataLoaded(true)
        dispatch(setUnload())
      })
      .catch((err) => {
        setDeposits([])
        setFilteredDeposits([])
        setDataLoaded(false)
        dispatch(setBanner(`Error fetching deposits - ${err}`, 'danger'))
        dispatch(setUnload())
      })
  }

  const transformApiDeposit = (apiDeposit) => {
    return {
      deposit_id: apiDeposit.id,
      beneficiary: apiDeposit.account?.beneficiary || 'N/A',
      account: apiDeposit.account?.number || 'N/A',
      deposit_date: apiDeposit.bank_date ? moment(apiDeposit.bank_date).format('MM/DD/YYYY') : '',
      date_entered: apiDeposit.last_updated_date ? moment(apiDeposit.last_updated_date).format('MM/DD/YYYY') : '',
      amount: apiDeposit.amount || '0.00',
      deposit_type: apiDeposit.deposit_type
        ? apiDeposit.deposit_type
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ')
        : '',
      fee_exceptions: apiDeposit.fee_exceptions ? 'Y' : 'N',
      fee_schedule: apiDeposit.fee_schedule || '',
      admin_fee: apiDeposit.fee_amount || '',
      fee_rate: apiDeposit.fee_rate || '',
      enroll_fee: apiDeposit.enrollment_fee_amount || '',
      owner: apiDeposit.owner || '',
      status: apiDeposit.status,
      next_status: apiDeposit.next_status,
      journal_name: apiDeposit.account?.journal_name || 'N/A',
      account_id: apiDeposit.account?.id || null,
      floor_amount: apiDeposit.account?.floor_amount || 0,
      has_budget: apiDeposit.account?.has_budget || false,
      locked_for_edit: apiDeposit.locked_for_edit || false,
    }
  }

  const filterDeposits = () => {
    dispatch(setLoad())
    let deps = filteredDeposits
    if (sort != null && sort.key != null) {
      deps = sortByKey(deps, sort.key, sort.type)
    }
    setFilteredDeposits(deps)
    dispatch(setUnload())
  }

  const onPageChange = (current) => {
    setPagination((prev) => ({
      ...prev,
      current,
    }))
  }

  const handleRowClick = (depositId) => {
    const queueName = getQueueNameFromStatus(nextStatus)
    navigate(`/deposits/${depositId}?from_queue=${queueName}`)
  }

  const isSecondReviewTab = (countKey) => {
    return countKey === 'reviewed_with_approved_edits'
  }

  const renderDepositRow = (deposit, index) => {
    const isPaidTable = ['deposited_today', 'deposited'].includes(nextStatus)

    return (
      <Row key={`deposit-${index}`} onClick={() => handleRowClick(deposit.deposit_id)} style={{ cursor: 'pointer' }}>
        <Cell value={deposit.beneficiary} />
        <Cell value={deposit.account} />
        <Cell value={deposit.deposit_date} />
        {isPaidTable && <Cell value={deposit.date_entered} />}
        <Cell value={`${deposit.amount}`} />
        <Cell value={deposit.deposit_type} />
        <Cell value={deposit.fee_exceptions} />
        {!isPaidTable && <Cell value={deposit.fee_schedule} />}
        <Cell value={deposit.admin_fee} />
        <Cell value={deposit.fee_rate ? `${deposit.fee_rate}%` : ''} />
        <Cell value={deposit.enroll_fee} />
        <Cell value={deposit.owner} />
        <Cell value={deposit.deposit_id} />
        <Cell
          value={
            <div className={styles.lockUnlockIcon}>
              <LockUnlockIcon lockedForEdit={deposit.locked_for_edit} />
            </div>
          }
        />
      </Row>
    )
  }

  useEffect(() => {
    filterDeposits()
  }, [sort, deposits])

  useEffect(() => {
    if (nextStatus != null) {
      getData()
    }
  }, [nextStatus, currentStatus, pagination.current, filterKey, selectedOptions])

  useEffect(() => {
    const initialTableId = tableId || 'deposit_queue'

    setCurrentStatus(initialTableId)
    const foundColor = summaryBoxNavList().find((li) => li.countKey === initialTableId)
    foundColor?.theme && setColor(foundColor.theme)

    if (tableId != null && subTableId != null) {
      const initialTabIndex = parseInt(subTableId, 10)
      const allAvailableTabs = getAllTabsForStatus(initialTableId)
      const validTabIndex = initialTabIndex < allAvailableTabs.length ? initialTabIndex : 0

      if (validTabIndex !== selectedTabIndex) {
        setSelectedTabIndex(validTabIndex)

        if (validTabIndex !== initialTabIndex) {
          navigateWithoutRedirect('stid', validTabIndex)
        }
      }
    }
  }, [tableId, subTableId])

  useEffect(() => {
    const urlTabIndex = query.get('stid')
    if (urlTabIndex) {
      const parsedIndex = parseInt(decodedData(urlTabIndex), 10)
      const allAvailableTabs = getAllTabsForStatus(currentStatus)

      if (parsedIndex < allAvailableTabs.length && parsedIndex !== selectedTabIndex) {
        setSelectedTabIndex(parsedIndex)
        const newStatus = allAvailableTabs[parsedIndex]?.nextStatus
        if (newStatus) {
          setNextStatus(newStatus)
        }
      }
    }
  }, [query.get('stid'), currentStatus])

  return (
    <div>
      <div className={styles.distributionHeaderWrapper}>
        <div className={styles.distributionHeader}>
          <div className={styles.distributionHeaderLeft}>
            <h3 className={styles.drfHeader}>$ Deposits</h3>
            <div>
              <p className={styles.headerDay}>Today</p>
              <h3 className={styles.headerDate}>{dateFormat(new Date())}</h3>
            </div>
          </div>
          <div className={styles.distributionHeaderRight}>
            <LinkButton className={styles.newRequestButton} to="/deposits/received?from_queue=receive">
              $ RECEIVE DEPOSIT
            </LinkButton>
            <form className={styles.drfSearch} onSubmit={navigateToDeposit}>
              <input
                className={styles.textInput}
                type="number"
                name="searchById"
                placeholder="Enter Deposit ID"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                required
              />
              <button type="submit" className={styles.button} disabled={!search || search.trim() === ''}>
                GO
              </button>
            </form>
          </div>
        </div>
      </div>
      <section className={styles.contentContainer}>
        <div className={styles.fullScreen}>
          <Tabs selectedIndex={selectedTabIndex} onSelect={handleTabSelect} className="tabsContainer">
            <ul className={styles.distributionHeaderNav}>
              {summaryBoxNavList().map((item, index) => (
                <li
                  key={`${item.countKey}-${item.title}-${index}`}
                  className={isNavActive(item.countKey === currentStatus)}
                  onClick={() => {
                    handleCurrentStatus(item.countKey, true)
                  }}
                >
                  {item.title}
                </li>
              ))}
            </ul>
            <TableFilter
              setFilterKey={setFilterKey}
              filterKey={filterKey}
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
              color={color}
            />
            <TabList className={[styles.tabList, 'react-tabs', colorTheme[color].bgMid ?? colorTheme[color].bgLight]}>
              {tabTitleList(currentStatus).map((item, index) => (
                <Tab
                  key={`${item.title}-${item.countKey}-${index}`}
                  className={[
                    styles.buttonLocal,
                    'react-tabs__tab',
                    'react-tabs__capitalize',
                    isSecondReviewTab(item.countKey) ? styles.redText : '',
                  ]}
                  style={isSecondReviewTab(item.countKey) ? { color: 'red' } : {}}
                >
                  {tabTitle(item.title, item.countKey)}
                </Tab>
              ))}
            </TabList>
            {tabTitleList(currentStatus).map((item) => (
              <TabPanel key={item.title}>
                <div className={styles.tableContainer}>
                  <Table
                    headers={headers({
                      currentStatus,
                      sort,
                      nextStatus,
                      handleFilter,
                      className: styles.tableHeader,
                    })}
                    tableSize="infinite"
                    tableType="read"
                  >
                    {filteredDeposits?.map((deposit, index) => renderDepositRow(deposit, index))}
                  </Table>
                </div>
              </TabPanel>
            ))}
          </Tabs>
          {filteredDeposits.length > 0 && ['deleted', 'deposited', 'cash'].includes(nextStatus) && (
            <Pagination align="center" onChange={onPageChange} total={pagination.total} pageSize={pagination.pageSize} current={pagination.current} />
          )}
        </div>
      </section>
    </div>
  )
}

export default withRouter(Deposits)
